import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Loader2, Copy, Eye, Save, ExternalLink } from 'lucide-react';

const FIELDS = [
  { key: 'name', label: 'Name', sample: 'Aliasger Mufaddal' },
  { key: 'its', label: 'ITS', sample: '30445237' },
  { key: 'mobile', label: 'Mobile', sample: '+91 98765 43210' },
];

const DEFAULT_POSITION = (key) => ({
  x_pct: 50,
  y_pct: key === 'name' ? 70 : key === 'its' ? 80 : 88,
  font_size_pct: key === 'name' ? 4.5 : 3.5,
  color: '#000000',
  align: 'center',
  max_width_pct: 90,
});

export default function EventCardDesigner({ open, onClose, api, event, onSaved }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [whatsappCaption, setWhatsappCaption] = useState('');
  const [templateB64, setTemplateB64] = useState('');
  const [templateW, setTemplateW] = useState(0);
  const [templateH, setTemplateH] = useState(0);
  const [positions, setPositions] = useState({
    name: DEFAULT_POSITION('name'),
    its: DEFAULT_POSITION('its'),
    mobile: DEFAULT_POSITION('mobile'),
  });
  const [activeField, setActiveField] = useState('name');
  const dragRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events/${event.id}/registration/config`);
      const cfg = res.data.registration_config || {};
      setConfig(cfg);
      setEnabled(!!cfg.enabled);
      setSlug(cfg.slug || '');
      setWelcomeMessage(cfg.welcome_message || '');
      setWhatsappCaption(cfg.whatsapp_caption || '');
      setTemplateB64(cfg.template_base64 || '');
      setTemplateW(cfg.template_width || 0);
      setTemplateH(cfg.template_height || 0);
      const pos = cfg.positions || {};
      setPositions({
        name: { ...DEFAULT_POSITION('name'), ...(pos.name || {}) },
        its: { ...DEFAULT_POSITION('its'), ...(pos.its || {}) },
        mobile: { ...DEFAULT_POSITION('mobile'), ...(pos.mobile || {}) },
      });
    } catch (e) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [api, event.id]);

  useEffect(() => { if (open) fetchConfig(); }, [open, fetchConfig]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a PNG or JPG image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const b64 = String(dataUrl).split(',')[1] || '';
      const img = new Image();
      img.onload = () => {
        setTemplateB64(b64);
        setTemplateW(img.naturalWidth);
        setTemplateH(img.naturalHeight);
        toast.success('Template image loaded. Click "Save All" to persist.');
      };
      img.onerror = () => toast.error('Could not read image');
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const onDragStart = (key) => (e) => {
    e.preventDefault();
    setActiveField(key);
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startPos = positions[key];
    const rect = containerRef.current.getBoundingClientRect();
    const onMove = (mv) => {
      const dx = ((mv.clientX - startMouseX) / rect.width) * 100;
      const dy = ((mv.clientY - startMouseY) / rect.height) * 100;
      const newX = Math.max(0, Math.min(100, startPos.x_pct + dx));
      const newY = Math.max(0, Math.min(100, startPos.y_pct + dy));
      setPositions(prev => ({ ...prev, [key]: { ...prev[key], x_pct: newX, y_pct: newY } }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const updateField = (key, prop, val) => {
    setPositions(prev => ({ ...prev, [key]: { ...prev[key], [prop]: val } }));
  };

  const handleSave = async () => {
    if (enabled && !templateB64) {
      toast.error('Please upload an ID card template before enabling registration');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled,
        slug: slug || null,
        positions,
        welcome_message: welcomeMessage,
        whatsapp_caption: whatsappCaption,
      };
      if (templateB64 && templateB64 !== (config?.template_base64 || '')) {
        payload.template_base64 = templateB64;
      }
      const res = await api.put(`/events/${event.id}/registration/config`, payload);
      toast.success('Registration configuration saved');
      setConfig(res.data.registration_config);
      if (res.data.registration_config?.slug) setSlug(res.data.registration_config.slug);
      if (onSaved) onSaved(res.data.registration_config);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const publicLink = slug ? `${window.location.origin}/register/${slug}` : '';

  const copyLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    toast.success('Public registration link copied');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="card-designer-title">Public Registration & ID Card – {event.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <Tabs defaultValue="settings" className="mt-2">
            <TabsList>
              <TabsTrigger value="settings" data-testid="card-designer-tab-settings">Settings</TabsTrigger>
              <TabsTrigger value="designer" data-testid="card-designer-tab-designer">ID Card Designer</TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="space-y-4 mt-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                <div>
                  <Label className="text-base font-semibold">Enable Public Registration</Label>
                  <p className="text-sm text-slate-500">When enabled, people can register without logging in.</p>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="card-designer-enabled-switch" />
              </div>

              <div>
                <Label htmlFor="reg-slug">Public URL Slug</Label>
                <div className="flex gap-2 mt-1">
                  <span className="px-3 py-2 rounded border bg-slate-100 text-sm text-slate-600 whitespace-nowrap">{window.location.origin}/register/</span>
                  <Input
                    id="reg-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="leave blank to auto-generate"
                    data-testid="card-designer-slug-input"
                  />
                </div>
                {publicLink && (
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <a href={publicLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                      {publicLink} <ExternalLink className="w-3 h-3" />
                    </a>
                    <Button size="sm" variant="ghost" onClick={copyLink} data-testid="card-designer-copy-link-btn">
                      <Copy className="w-3 h-3 mr-1" /> Copy
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="welcome-msg">Welcome Message (shown above the form)</Label>
                <textarea
                  id="welcome-msg"
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                  placeholder="Optional: e.g., Salaam Mumineen! Please register to attend..."
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
                  data-testid="card-designer-welcome-textarea"
                />
              </div>

              <div>
                <Label htmlFor="wa-caption">WhatsApp Caption (sent with the ID card)</Label>
                <textarea
                  id="wa-caption"
                  value={whatsappCaption}
                  onChange={(e) => setWhatsappCaption(e.target.value)}
                  placeholder={`Default: Thank you {Name} for registering for ${event.name}. Please show this ID card at the venue.`}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
                  data-testid="card-designer-caption-textarea"
                />
              </div>
            </TabsContent>

            <TabsContent value="designer" className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="card-designer-file-input"
                />
                <Button onClick={() => fileInputRef.current?.click()} variant="outline" data-testid="card-designer-upload-btn">
                  <Upload className="w-4 h-4 mr-2" /> Upload Template
                </Button>
                <span className="text-sm text-slate-500">PNG/JPG, recommended 1080×1920 or any portrait/landscape size</span>
              </div>

              {!templateB64 ? (
                <div className="border-2 border-dashed rounded-xl p-12 text-center text-slate-500">
                  Upload an ID card template image to begin. Drag the labels onto the spots where Name, ITS and Mobile should appear.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Canvas */}
                  <div className="lg:col-span-2">
                    <div
                      ref={containerRef}
                      className="relative border rounded-xl bg-slate-100 overflow-hidden select-none"
                      style={{ aspectRatio: templateW && templateH ? `${templateW} / ${templateH}` : '3 / 4' }}
                      data-testid="card-designer-canvas"
                    >
                      <img
                        src={`data:image/png;base64,${templateB64}`}
                        alt="Template"
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                      />
                      {FIELDS.map((f) => {
                        const pos = positions[f.key];
                        const isActive = activeField === f.key;
                        const fontSizePx = (pos.font_size_pct / 100) * (containerRef.current?.offsetHeight || 400);
                        return (
                          <div
                            key={f.key}
                            onMouseDown={onDragStart(f.key)}
                            className={`absolute cursor-move whitespace-nowrap font-bold ${isActive ? 'ring-2 ring-blue-500 ring-offset-1' : 'ring-1 ring-blue-300/40'}`}
                            style={{
                              left: `${pos.x_pct}%`,
                              top: `${pos.y_pct}%`,
                              transform: pos.align === 'center' ? 'translate(-50%, -50%)' : (pos.align === 'right' ? 'translate(-100%, -50%)' : 'translate(0, -50%)'),
                              fontSize: `${fontSizePx}px`,
                              color: pos.color,
                              padding: '2px 6px',
                              backgroundColor: isActive ? 'rgba(59, 130, 246, 0.10)' : 'transparent',
                              borderRadius: '4px',
                            }}
                            data-testid={`card-designer-label-${f.key}`}
                          >
                            {f.sample}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Drag the colored labels to position each field. Adjust size and color in the panel on the right.</p>
                  </div>

                  {/* Field controls */}
                  <div className="space-y-3">
                    {FIELDS.map((f) => {
                      const pos = positions[f.key];
                      const isActive = activeField === f.key;
                      return (
                        <div
                          key={f.key}
                          className={`border rounded-lg p-3 cursor-pointer ${isActive ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200'}`}
                          onClick={() => setActiveField(f.key)}
                          data-testid={`card-designer-field-${f.key}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">{f.label}</span>
                            <span className="text-xs text-slate-500">{Math.round(pos.x_pct)}%, {Math.round(pos.y_pct)}%</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <Label className="text-xs">Font size %</Label>
                              <Input
                                type="number"
                                step="0.1"
                                min="1"
                                max="20"
                                value={pos.font_size_pct}
                                onChange={(e) => updateField(f.key, 'font_size_pct', parseFloat(e.target.value) || 3)}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Color</Label>
                              <Input
                                type="color"
                                value={pos.color}
                                onChange={(e) => updateField(f.key, 'color', e.target.value)}
                                className="h-8 p-1 cursor-pointer"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Align</Label>
                              <select
                                value={pos.align}
                                onChange={(e) => updateField(f.key, 'align', e.target.value)}
                                className="h-8 w-full text-xs border rounded px-2"
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} data-testid="card-designer-close-btn">Close</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="card-designer-save-btn">
            {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : (<><Save className="w-4 h-4 mr-2" /> Save All</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
